import { Button, Card, message, Modal, Popconfirm, Select, Space, Table, Typography } from "antd";
import { useEffect, useState } from "react";
import { serverFetch } from "@/server";
import { selectFilterOption } from "@/extensions/helper";
import { dictsGetUsers, userOptionsWithPosition } from "@/extensions/dicts";
import { isMobile } from "@/extensions/design";
import "../users/user.css";

const { Text } = Typography;

const ProductExecutors = ({ productId, productVid }) => {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [productCntList, setProductCntList] = useState([]);
  const [productCntLoading, setProductCntLoading] = useState(false);
  const [missingServiceNames, setMissingServiceNames] = useState({});
  const [modalVisible, setModalVisible] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [deleteConfirmLoading, setDeleteConfirmLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [productName, setProductName] = useState("");

  const getData = () => {
    if (!productId) return;
    setLoading(true);
    serverFetch(`User/ProductUsers/${productId}`)
      .then(data => {
        setItems(data);
        setLoading(false);
      })
      .catch(e => {
        setLoading(false);
        message.error(e.userMessage);
      });
  };

  useEffect(getData, [productId]);

  useEffect(() => {
    setUsersLoading(true);
    dictsGetUsers(setUsers, { activeOnly: true });
    setUsersLoading(false);
  }, []);

  useEffect(() => {
    if (!productId || (productVid !== 2 && productVid !== 3)) {
      setProductCntList([]);
      return;
    }
    setProductCntLoading(true);
    serverFetch(`product/CntList/${productId}`)
      .then(data => {
        setProductCntList(data || []);
        setProductCntLoading(false);
      })
      .catch(e => {
        message.error(e.userMessage);
        setProductCntList([]);
        setProductCntLoading(false);
      });
  }, [productId, productVid]);

  useEffect(() => {
    if (!productId) return;
    serverFetch(`product/${productId}`)
      .then(data => setProductName(data.name || ""))
      .catch(() => setProductName(""));
  }, [productId]);

  const onAdd = () => {
    if (!selectedUserId) {
      message.warning("Выберите исполнителя");
      return;
    }

    if (productVid === 2 || productVid === 3) {
      if (productCntLoading) {
        message.warning("Загрузка состава пакета...");
        return;
      }
    }

    setLoading(true);
    if (!productCntList.length) {
      serverFetch("User/ProductAdd", {
        method: "POST",
        bodyData: { userId: selectedUserId, productId: productId }
      })
        .then(() => {
          message.success(`Исполнитель добавлен в «${productName || productId}»`);
          setSelectedUserId(null);
          getData();
        })
        .catch(e => message.error(e.userMessage))
        .finally(() => setLoading(false));
      return;
    }

    serverFetch(`User/ProductList/${selectedUserId}`)
      .then(userProducts => {
        const missingIds = productCntList
          .map(cnt => cnt.productId)
          .filter(pid => !userProducts.some(up => up.productId === pid));

        if (missingIds.length === 0) {
          return serverFetch("User/ProductAdd", {
            method: "POST",
            bodyData: { userId: selectedUserId, productId: productId }
          }).then(() => {
            message.success(`Исполнитель добавлен в «${productName || productId}»`);
            setSelectedUserId(null);
            getData();
          });
        }

        const namesMap = {};
        const firstPid = missingIds[0];
        const rest = missingIds.slice(1);
        return serverFetch(`product/${firstPid}`)
          .then(product => { namesMap[firstPid] = product.name || `#${firstPid}`; })
          .catch(() => { namesMap[firstPid] = `#${firstPid}`; })
          .then(() => {
            if (rest.length === 0) return;
            function fetchNext(index) {
              if (index >= rest.length) return;
              return serverFetch(`product/${rest[index]}`)
                .then(product => { namesMap[rest[index]] = product.name || `#${rest[index]}`; })
                .catch(() => { namesMap[rest[index]] = `#${rest[index]}`; })
                .then(() => fetchNext(index + 1));
            }
            return fetchNext(0);
          })
          .then(() => {
            setMissingServiceNames(namesMap);
            setModalVisible(true);
          });
      })
      .catch(e => message.error(e.userMessage))
      .finally(() => setLoading(false));
  };

  const handleModalOk = () => {
    setConfirmLoading(true);
    const missingIds = Object.keys(missingServiceNames).map(Number);
    function addNext(index) {
      if (index >= missingIds.length) {
        return serverFetch("User/ProductAdd", {
          method: "POST",
          bodyData: { userId: selectedUserId, productId: productId }
        });
      }
      return serverFetch("User/ProductAdd", {
        method: "POST",
        bodyData: { userId: selectedUserId, productId: missingIds[index] }
      }).then(() => addNext(index + 1));
    }
    addNext(0)
      .then(() => {
        message.success(`Исполнитель добавлен в «${productName || productId}» и услуги`);
        setModalVisible(false);
        setMissingServiceNames({});
        setSelectedUserId(null);
        getData();
      })
      .catch(e => message.error(e.userMessage))
      .finally(() => setConfirmLoading(false));
  };

  const handleModalCancel = () => {
    setModalVisible(false);
    setMissingServiceNames({});
  };

  const onDelete = (userProductId) => {
    const record = items.find(i => i.id === userProductId);
    if (!record) return;

    if (productVid === 2 || productVid === 3) {
      if (productCntLoading) {
        message.warning("Загрузка состава пакета...");
        return;
      }
      if (productCntList.length) {
        const serviceIds = productCntList.map(cnt => cnt.productId);
        serverFetch(`User/ProductList/${record.userId}`)
          .then(userProducts => {
            const serviceUserProducts = userProducts.filter(up => serviceIds.includes(up.productId));
            if (serviceUserProducts.length > 0) {
              const serviceNames = {};
              serviceUserProducts.forEach(up => {
                serviceNames[up.id] = up.productName || `#${up.productId}`;
              });
              setDeleteTarget({
                userProductId,
                userId: record.userId,
                serviceUserProductIds: serviceUserProducts.map(up => up.id),
                serviceNames,
              });
              setDeleteModalVisible(true);
            } else {
              performDelete(userProductId);
            }
          })
          .catch(e => message.error(e.userMessage));
        return;
      }
    }

    performDelete(userProductId);
  };

  const performDelete = (userProductId) => {
    setLoading(true);
    serverFetch(`User/ProductDelete/${userProductId}`, { method: "DELETE" })
      .then(() => {
        setItems(prev => prev.filter(item => item.id !== userProductId));
        getData();
        message.success("Исполнитель удален");
      })
      .catch(e => message.error(e.userMessage))
      .finally(() => setLoading(false));
  };

  const handleDeleteConfirm = () => {
    if (!deleteTarget) return;
    setDeleteConfirmLoading(true);
    const serviceIds = deleteTarget.serviceUserProductIds;
    function deleteNext(index) {
      if (index >= serviceIds.length) {
        return serverFetch(`User/ProductDelete/${deleteTarget.userProductId}`, { method: "DELETE" });
      }
      return serverFetch(`User/ProductDelete/${serviceIds[index]}`, { method: "DELETE" }).then(() => deleteNext(index + 1));
    }
    deleteNext(0)
      .then(() => {
        setItems(prev => prev.filter(item => item.id !== deleteTarget.userProductId && !deleteTarget.serviceUserProductIds.includes(item.id)));
        setDeleteModalVisible(false);
        setDeleteTarget(null);
        getData();
        message.success("Исполнитель удален из пакета и услуг");
      })
      .catch(e => message.error(e.userMessage))
      .finally(() => setDeleteConfirmLoading(false));
  };

  const handleDeleteCancel = () => {
    setDeleteModalVisible(false);
    setDeleteTarget(null);
  };

  const columns = [
    { title: "ФИО", dataIndex: "userName" },
    {
      title: "Действие",
      key: "action",
      width: 100,
      render: (_, record) => (
        <Popconfirm
          title="Убрать исполнителя?"
          description="Это действие нельзя отменить."
          onConfirm={() => onDelete(record.id)}
          okText="Да"
          cancelText="Нет"
        >
          <Button size="small" danger>Удалить</Button>
        </Popconfirm>
      ),
    },
  ];

  return !isMobile ? (
    <div className="sport-section">
      <Space style={{ marginBottom: 16 }}>
        <Select
          style={{ width: 300 }}
          placeholder="Выберите исполнителя"
          options={userOptionsWithPosition(users)}
          loading={usersLoading}
          showSearch
          filterOption={selectFilterOption}
          optionLabelProp="title"
          value={selectedUserId}
          onChange={setSelectedUserId}
          allowClear
        />
        <Button onClick={onAdd} loading={loading} disabled={!selectedUserId || modalVisible || deleteModalVisible}>
          Добавить
        </Button>
      </Space>
      <Table
        columns={columns}
        size="small"
        dataSource={items}
        loading={loading}
        pagination={false}
        rowKey="id"
      />
      <Modal
        title="Подтверждение"
        open={modalVisible}
        onCancel={handleModalCancel}
        confirmLoading={confirmLoading}
        footer={[
          <Button key="cancel" onClick={handleModalCancel} disabled={confirmLoading}>
            Нет
          </Button>,
          <Button key="ok" type="primary" onClick={handleModalOk} loading={confirmLoading}>
            Да
          </Button>,
        ]}
      >
        <p>Пользователь не может оказывать услугу, указанную в пакете. Добавить его как исполнителя услуги?</p>
        {Object.keys(missingServiceNames).length > 0 && (
          <ul>
            {Object.entries(missingServiceNames).map(([pid, name]) => (
              <li key={pid}>{name}</li>
            ))}
          </ul>
        )}
      </Modal>
      <Modal
        title="Подтверждение"
        open={deleteModalVisible}
        onCancel={handleDeleteCancel}
        confirmLoading={deleteConfirmLoading}
        footer={[
          <Button key="cancel" onClick={handleDeleteCancel} disabled={deleteConfirmLoading}>
            Нет
          </Button>,
          <Button key="ok" type="primary" onClick={handleDeleteConfirm} loading={deleteConfirmLoading}>
            Да
          </Button>,
        ]}
      >
        <p>Пользователь является исполнителем следующих услуг из этого пакета. Удалить его из них тоже?</p>
        {deleteTarget && Object.keys(deleteTarget.serviceNames).length > 0 && (
          <ul>
            {Object.entries(deleteTarget.serviceNames).map(([id, name]) => (
              <li key={id}>{name}</li>
            ))}
          </ul>
        )}
      </Modal>
    </div>
  ) : (
    <div className="product-mobile-container">
      <div className="product-mobile-actions" style={{ display: "flex", gap: 8 }}>
        <Select
          style={{ flex: 1 }}
          placeholder="Выберите исполнителя"
          options={userOptionsWithPosition(users)}
          loading={usersLoading}
          showSearch
          filterOption={selectFilterOption}
          optionLabelProp="title"
          value={selectedUserId}
          onChange={setSelectedUserId}
          allowClear
        />
        <Button onClick={onAdd} loading={loading} disabled={!selectedUserId || modalVisible || deleteModalVisible}>
          Добавить
        </Button>
      </div>
      <div className="product-mobile-scroll">
        {items.map(item => (
          <Card key={item.id} className="product-packet-card-mobile" style={{ borderLeft: "4px solid #1677ff" }}>
            <div className="product-packet-card-row1-mobile">
              <Text strong>{item.userName}</Text>
              <Popconfirm
                title="Убрать исполнителя?"
                description="Это действие нельзя отменить."
                onConfirm={() => onDelete(item.id)}
                okText="Да"
                cancelText="Нет"
              >
                <Button size="small" danger>Удалить</Button>
              </Popconfirm>
            </div>
          </Card>
        ))}
        {items.length === 0 && !loading && <div className="product-packet-empty-mobile">Нет данных</div>}
      </div>
      <Modal
        title="Подтверждение"
        open={modalVisible}
        onCancel={handleModalCancel}
        confirmLoading={confirmLoading}
        footer={[
          <Button key="cancel" onClick={handleModalCancel} disabled={confirmLoading}>
            Нет
          </Button>,
          <Button key="ok" type="primary" onClick={handleModalOk} loading={confirmLoading}>
            Да
          </Button>,
        ]}
      >
        <p>Пользователь не может оказывать услугу, указанную в пакете. Добавить его как исполнителя услуги?</p>
        {Object.keys(missingServiceNames).length > 0 && (
          <ul>
            {Object.entries(missingServiceNames).map(([pid, name]) => (
              <li key={pid}>{name}</li>
            ))}
          </ul>
        )}
      </Modal>
      <Modal
        title="Подтверждение"
        open={deleteModalVisible}
        onCancel={handleDeleteCancel}
        confirmLoading={deleteConfirmLoading}
        footer={[
          <Button key="cancel" onClick={handleDeleteCancel} disabled={deleteConfirmLoading}>
            Нет
          </Button>,
          <Button key="ok" type="primary" onClick={handleDeleteConfirm} loading={deleteConfirmLoading}>
            Да
          </Button>,
        ]}
      >
        <p>Пользователь является исполнителем следующих услуг из этого пакета. Удалить его из них тоже?</p>
        {deleteTarget && Object.keys(deleteTarget.serviceNames).length > 0 && (
          <ul>
            {Object.entries(deleteTarget.serviceNames).map(([id, name]) => (
              <li key={id}>{name}</li>
            ))}
          </ul>
        )}
      </Modal>
    </div>
  );
};

export default ProductExecutors;
